import React from 'react';
import { Coordinates, Segment, Muffe, Fuse, TrafoStation, Kabelverteilschrank } from '../../types/network';
import {
  Abgangsleitung,
  Hausanschluss,
} from '../../types/network';
import { useNetworkElementsStore } from '../../store/networkElementsStore';
import { useNetworkUIStore } from '../../store/networkUIStore';
import { useStressTestStore } from '../../store/stressTestStore';
import { useViewMode } from '../../context/useViewMode';
import { detailStyles } from '../../styles/detailStyles';
import { fmt } from '../../utils/numberFormat';
import { cableDisplayName, cableTypeLabel } from '../../utils/elementDisplayName';

const findClosestSegment = (muffeCoords: Coordinates, segments: Segment[]): Segment | null => {
  if (!segments?.length || !muffeCoords) return null;

  const firstSegment = segments[0];
  const lastSegment = segments[segments.length - 1];

  if (!firstSegment?.startCoordinate || !lastSegment?.endCoordinate) return null;

  try {
    const distToStart = Math.sqrt(
      Math.pow(muffeCoords.lat - firstSegment.startCoordinate.lat, 2) +
      Math.pow(muffeCoords.lng - firstSegment.startCoordinate.lng, 2)
    );
    const distToEnd = Math.sqrt(
      Math.pow(muffeCoords.lat - lastSegment.endCoordinate.lat, 2) +
      Math.pow(muffeCoords.lng - lastSegment.endCoordinate.lng, 2)
    );

    const segmentIndex = distToStart < distToEnd ? 0 : segments.length - 1;
    return segments[segmentIndex] || null;
  } catch (error) {
    console.error('[findClosestSegment] Error calculating distances:', error);
    return null;
  }
};

// Find segments connected to a muffe by checking if any segment's fromBus or toBus matches the muffe's busIndex
const findSegmentsConnectedToMuffe = (muffe: Muffe, cable: Abgangsleitung): Segment[] => {
  if (muffe.busIndex === undefined || muffe.busIndex === null) return [];
  if (!cable.segments?.length) return [];

  return cable.segments.filter(seg =>
    seg.fromBus === muffe.busIndex || seg.toBus === muffe.busIndex
  );
};

// Check if a cable is connected to a muffe via busIndex
const isCableConnectedToMuffe = (muffe: Muffe, cable: Abgangsleitung): boolean => {
  return findSegmentsConnectedToMuffe(muffe, cable).length > 0;
};

interface Props {
  elementId: string;
}

const LineSegmentDisplay: React.FC<{ segment: Segment; index: number; total: number }> = ({ segment, index }) => (
  <div className="bg-white dark:bg-primary-600 px-2 py-1" title={segment.id}>
    <div className="flex justify-between text-sm text-gray-900 dark:text-primary-100">
      <span>Segment {index + 1}</span>
      <span>{fmt(segment.length, 1)} m</span>
    </div>
  </div>
);

export const ConnectedAbgangsleitungen: React.FC<Props> = ({ elementId }) => {
  const viewMode = useViewMode();
  const isStressTestMode = viewMode === 'stress-test';
  const stressElements = useStressTestStore(state => state.stressElements);
  const stressTestResults = useStressTestStore(state => state.stressTestResults);
  const { elements: globalElements } = useNetworkElementsStore();
  const elements = isStressTestMode ? stressElements : globalElements;
  const segments = useNetworkElementsStore(state => state.segments);
  const setSelectedElement = useNetworkUIStore(state => state.setSelectedElement);
  const resolveElement = React.useCallback(
    (id: string) => elements.find(el => el.id === id),
    [elements],
  );

  const element = React.useMemo(() => elements.find(el => el.id === elementId), [elements, elementId]);

  const connectedLines = React.useMemo(() => {
    if (!element) return [];

    if (element.type === 'hausanschluss') {
      const house = element as Hausanschluss;
      const connectedLineId = house.connectedLine?.id;
      if (connectedLineId) {
        const connectedLine = elements.find(el => el.id === connectedLineId);
        return connectedLine && connectedLine.type === 'abgangsleitung' ? [connectedLine as Abgangsleitung] : [];
      }
      return [];
    }

    if (element.type === 'muffe') {
      const muffe = element as Muffe;
      // Find cables connected via busIndex from cable_segments (from_bus/to_bus)
      return elements.filter((el): el is Abgangsleitung =>
        el.type === 'abgangsleitung' && isCableConnectedToMuffe(muffe, el as Abgangsleitung)
      );
    }

    return elements.filter((el): el is Abgangsleitung =>
      el.type === 'abgangsleitung' &&
      (el.sourceId === elementId || el.targetId === elementId)
    );
  }, [elements, elementId, element]);

  // Look up fuse for a cable from the parent element
  const getFuseForCable = (cableId: string): Fuse | undefined => {
    if (!element) return undefined;
    if (element.type === 'trafostation') {
      return (element as TrafoStation).fuses?.[cableId];
    }
    if (element.type === 'kabelverteilschrank') {
      // KVS connectedLines carry fuse on each ConnectedLine
      const cl = (element as Kabelverteilschrank).connectedLines?.find(c => c.id === cableId);
      return cl?.fuse;
    }
    return undefined;
  };

  if (!connectedLines.length) {
    return (
      <div className={detailStyles.card.wrapper}>
        <div className={detailStyles.card.header}>
          <h3 className={detailStyles.card.headerTitle}>
            <svg className={detailStyles.card.headerIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Verbundene Leitungen
          </h3>
        </div>
        <div className={detailStyles.card.body}>
          <p className={detailStyles.text.muted}>Keine verbundenen Leitungen gefunden</p>
        </div>
      </div>
    );
  }

  return (
    <div className={detailStyles.card.wrapper}>
      <div className={detailStyles.card.header}>
        <h3 className={detailStyles.card.headerTitle}>
          <svg className={detailStyles.card.headerIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          Verbundene Leitungen
        </h3>
      </div>
      <div className={`${detailStyles.card.body} ${detailStyles.layout.fields}`}>
        {connectedLines.map(line => {
          let segment: Segment | null = null;
          let connectedSegments: Segment[] = [];

          if (element) {
            if (element.type === 'muffe') {
              const muffe = element as Muffe;
              // Find segments connected to this muffe via busIndex
              connectedSegments = findSegmentsConnectedToMuffe(muffe, line);
              if (connectedSegments.length > 0) {
                segment = connectedSegments[0];
              }
            } else if (line.segments?.length > 0) {
              segment = findClosestSegment(element.coordinates, line.segments);
            }
          }
          const stressData = segment?.id ? stressTestResults?.results?.[segment.id] : undefined;
          const power = isStressTestMode
            ? (stressData?.powerKw ?? null)
            : (segment?.id ? segments[segment.id]?.powerKW : null);
          const loading = isStressTestMode
            ? (stressData?.loadingPercent ?? null)
            : (segment?.id ? segments[segment.id]?.loadingPercent : null);

          const cableFuse = getFuseForCable(line.id);
          const showFuseInfo = element?.type === 'trafostation' || element?.type === 'kabelverteilschrank';

          const shownSegmentCount = element?.type === 'muffe'
            ? connectedSegments.length
            : (line.segments?.length ?? 0);

          return (
            <div
              key={line.id}
              onClick={() => setSelectedElement(line)}
              title="Kabel auswählen"
              className="bg-gray-50 dark:bg-primary-700 rounded-lg overflow-hidden border border-gray-200 dark:border-primary-600 cursor-pointer hover:border-gray-300 dark:hover:border-primary-500 transition-colors"
            >
              {/* Header */}
              <div className="p-2">
                <div className="flex items-center mb-1">
                  <div className={`w-2 h-2 rounded-full ${line.isEnabled ? 'bg-green-500 dark:bg-green-400' : 'bg-red-500 dark:bg-red-400'} mr-2 shrink-0`} />
                  <span className="font-medium text-gray-900 dark:text-primary-100 truncate" title={line.name}>
                    {cableDisplayName(line, resolveElement)}
                  </span>
                  {showFuseInfo && cableFuse && (
                    <span className={`ml-2 shrink-0 text-[10px] px-1.5 py-0.5 rounded ${
                      cableFuse.isInserted
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                    }`}>
                      {cableFuse.type} {cableFuse.current}A{!cableFuse.isInserted ? ' (nicht eingelegt)' : ''}
                    </span>
                  )}
                  <div className="ml-auto shrink-0 flex items-center gap-2 pl-2">
                    <span className={`font-medium ${power && power > 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-900 dark:text-primary-100'}`}>
                      {fmt(Math.abs(power ?? 0), 1)} kW
                    </span>
                    <span className="text-sm text-gray-500 dark:text-primary-400">Leistungsfluss</span>
                  </div>
                </div>
                <div className="text-sm text-gray-500 dark:text-primary-400">
                  Status: {line.isEnabled ? 'Aktiv' : 'Inaktiv'}
                </div>
              </div>

              {/* Cable Info */}
              <div className="bg-gray-50 dark:bg-primary-600 px-2 py-1 text-sm border-t border-gray-100 dark:border-primary-600">
                <div className="flex text-sm">
                  <div className="w-1/3">
                    <div className="text-gray-500 dark:text-primary-400">Kabeltyp</div>
                    <div className="mt-0.5 text-gray-900 dark:text-primary-100">{cableTypeLabel(line)}</div>
                  </div>
                  <div className="w-1/3">
                    <div className="text-gray-500 dark:text-primary-400">Querschnitt / Auslastung</div>
                    <div className="mt-0.5 text-gray-900 dark:text-primary-100">{line.crossSection} mm²{loading ? ` / ${fmt(loading, 0)}` : ' / 0'} %</div>
                  </div>
                  <div className="w-1/3 text-right">
                    <div className="text-gray-500 dark:text-primary-400">Gesamtlänge</div>
                    <div className="mt-0.5 text-gray-900 dark:text-primary-100">{fmt(line.totalLength, 1)} m</div>
                  </div>
                </div>
              </div>

              {/* Cable Segments */}
              <div className="px-2 py-1 border-t border-gray-100 dark:border-primary-600">
                <div className="text-gray-500 dark:text-primary-400 text-sm">Verbundene Segmente ({shownSegmentCount})</div>
                <div className="mt-0.5 space-y-0.5">
                  {element?.type === 'muffe' ? (
                    // Show segments connected to this muffe via busIndex
                    connectedSegments.map((seg) => (
                      <LineSegmentDisplay
                        key={seg.id}
                        segment={seg}
                        index={line.segments.indexOf(seg)}
                        total={line.segments?.length ?? 0}
                      />
                    ))
                  ) : (
                    line.segments?.map((seg, idx) => (
                      <LineSegmentDisplay
                        key={`${line.id}-${idx}`}
                        segment={seg}
                        index={idx}
                        total={line.segments?.length ?? 0}
                      />
                    ))
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
